import { getRequest } from '@/api/requests'
import useAuth from '@/utils/useAuth'
import useResources from '@/utils/useResources'
import { createRouter, createWebHistory } from 'vue-router'
import { useStore } from 'vuex'

const routes = [
  {
    path: '/',
    name: 'Home',
    redirect: '/applications/list',
  },
  {
    path: '/common-requests',
    name: 'CommonRequests',
    redirect: '/common-requests/list',
    children: [
      {
        path: 'list',
        name: 'CommonRequestsView',
        meta: { title: 'Общие заявки' },
        component: () => import('../views/CommonRequestsView.vue'),
      },
      {
        path: 'create',
        name: 'CommonRequestCreate',
        meta: { title: 'Создать общую заявку' },
        component: () => import('../components/CommonRequestCreate.vue'),
      },
      {
        path: 'view/:id',
        name: 'CommonRequestDetail',
        meta: { title: 'Просмотр заявки' },
        component: () => import('../components/CommonRequestDetail.vue'),
      },
      {
        path: 'edit/:id',
        name: 'CommonRequestEdit',
        meta: { title: 'Редактировать общую заявку' },
        component: () => import('../views/CommonRequestEdit.vue'),
      },
    ],
  },
  {
    path: '/auth',
    name: 'Auth',
    meta: { requiresAuth: false },
    component: () => import('../views/Auth.vue'),
  },
  {
    path: '/change-password',
    name: 'ChangePassword',
    component: () => import('../views/ChangePasswordView.vue'),
  },
  {
    path: '/applications',
    name: 'Requests',
    redirect: '/applications/list',
    children: [
      {
        path: 'list',
        name: 'RequestsView',
        meta: { title: 'Заявки' },
        component: () => import('../views/RequestsView.vue'),
      },
      {
        path: 'create',
        name: 'RequestCreate',
        meta: { title: 'Создать заявку' },
        component: () => import('../components/requests/RequestCreate.vue'),
        beforeEnter: async (to) => {
          const { store } = to.query
          // задаем пропсы
          to.matched[1].props.default = {
            code: +store,
          }
        },
      },
      {
        path: 'edit/:id/draft',
        name: 'RequestEditDraft',
        meta: { title: 'Редактировать черновик' },
        component: () => import('../components/requests/RequestUpdate.vue'),
        beforeEnter: async (to) => {
          const store = useStore()
          const {
            store: id,
            gi,
            comments,
            installation,
            dismantling,
          } = store.state.drafts.drafts[to.params.id]

          // задаем пропсы
          to.matched[1].props.default = {
            code: id,
            potentialGI: gi,
            comment: comments,
            installation,
            dismantling,
          }
        },
      },
      {
        path: 'edit/:id',
        name: 'RequestEdit',
        meta: { title: 'Редактировать заявку' },
        component: () => import('../components/requests/RequestUpdate.vue'),
        beforeEnter: async (to) => {
          const {
            store: { id },
            gi,
            gi_potential,
            comments,
            installation,
            dismantling,
            request_type,
            owner_type,
            coordination_with,
          } = await getRequest(to.params.id)
          // задаем пропсы
          to.matched[1].props.default = {
            code: id,
            GI: gi,
            potentialGI: gi_potential,
            requestType: request_type,
            comment: comments,
            installation,
            dismantling,
            ownerType: owner_type,
            coordinationWith: coordination_with,
          }
        },
      },
      {
        path: 'view/:id',
        name: 'RequestDetail',
        meta: { title: 'Просмотр заявки' },
        component: () => import('../components/requests/RequestDetail.vue'),
      },
      {
        path: 'view/:id/draft',
        name: 'RequestDetailDraft',
        meta: { title: 'Просмотр заявки' },
        component: () => import('../components/requests/RequestDetail.vue'),
      },
      {
        path: 'view/:id/approve',
        name: 'RequestApprove',
        meta: { title: 'Подписать заявку' },
        component: () => import('../components/requests/RequestApprove.vue'),
      },
      {
        path: 'view/:id/revoke',
        name: 'RequestRevoke',
        meta: { title: 'Отозвать подпись' },
        component: () => import('../components/requests/RequestRevoke.vue'),
      },
      {
        path: 'view/:id/photoreports',
        name: 'PhotoreportDetail',
        meta: { title: 'Просмотр фотоотчетов заявки' },
        component: () =>
          import('../components/photoreports/PhotoreportDetail.vue'),
      },
      {
        path: 'view/:id/photoreports/photo',
        name: 'PhotoreportPhoto',
        meta: { title: 'Просмотр фото фотоотчетов' },
        component: () =>
          import('../components/photoreports/PhotoreportPhoto.vue'),
      },
      {
        path: 'view/:id/tasks/:taskId',
        name: 'TaskDetail',
        meta: { title: 'Просмотр задания' },
        component: () => import('../components/tasks/TaskDetail.vue'),
      },
      {
        path: 'history',
        name: 'RequestsAssignmentHistory',
        meta: { title: 'История изменений' },
        component: () => import('../views/RequestsAssignmentHistoryView.vue'),
      },
      {
        path: 'redo/:layoutId/:photobindingId',
        name: 'RedoPhotobinding',
        meta: { title: 'Список версий' },
        component: () => import('../views/RedoPhotobindingView.vue'),
      },
    ],
  },
  {
    path: '/routes',
    redirect: '/routes/list',
    name: 'Routes',
    children: [
      {
        path: 'list',
        name: 'RoutesList',
        meta: { title: 'Маршруты' },
        component: () => import('../views/RoutesView.vue'),
      },
      {
        path: 'view/:routeId',
        name: 'RouteDetail',
        meta: { title: 'Просмотр маршрута' },
        component: () => import('../components/layouts/LayoutsDetail.vue'),
      },
      {
        path: '/routes/:segmentId',
        name: 'SegmentView',
        meta: { title: 'Просмотр сегмента' },
        component: () => import('../views/SegmentStoresView.vue'),
      },
    ],
  },
  {
    path: '/cross-analytics',
    name: 'CrossAnalytics',
    meta: { title: 'Сквозная аналитика' },
    component: () => import('../views/CrossAnalyticsView.vue'),
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
})

router.beforeEach(async (to) => {
  const requiresAuth = to.matched.every(
    (record) => record.meta?.requiresAuth !== false
  )

  const { ensureAuthenticated, mustChangePassword } = useAuth()
  let isAuthorized = false
  try {
    isAuthorized = await ensureAuthenticated()
  } catch (error) {
    console.error(error)
    isAuthorized = false
  }

  if (requiresAuth && !isAuthorized) {
    return { name: 'Auth' }
  }

  if (
    isAuthorized &&
    mustChangePassword.value &&
    to.name !== 'ChangePassword'
  ) {
    return {
      name: 'ChangePassword',
      query: { temporaryPassword: true },
    }
  }

  if (
    isAuthorized &&
    !mustChangePassword.value &&
    to.name === 'ChangePassword'
  ) {
    return { name: 'Home' }
  }

  if (!requiresAuth && isAuthorized) {
    return { name: 'Home' }
  }
})

router.beforeResolve(async (to) => {
  const requiresAuth = to.matched.every(
    (record) => record.meta?.requiresAuth !== false
  )

  if (!requiresAuth || to.name === 'ChangePassword') return
  const { mustChangePassword } = useAuth()
  if (mustChangePassword.value) return

  const { loadResources } = useResources()
  try {
    await loadResources()
  } catch (error) {
    console.error(error)
  }
})

export default router
